import { DateTime } from 'luxon';
import {
  HospitalPrescriptionRefillStatus,
  HospitalPrescriptionStatus,
} from '@common/models/Enums';
import { StringUtility } from '@common/utils';
import { defaultTo } from 'lodash';
import { IModel, ModelRecord, PhotoModel } from '@common/models';
import { HospitalPrescriptionModel } from './HospitalPrescriptionModel';

export interface IHospitalPrescriptionRequestListingModel extends IModel {
  hospitalPrescriptionId: number | undefined;
  hospitalPrescriptionStatusId: HospitalPrescriptionStatus | undefined;
  hospitalPrescriptionRequestSourceId: number | undefined;
  hospitalPrescriptionRefillId: number | undefined;
  hospitalPrescriptionRefillStatusId:
    | HospitalPrescriptionRefillStatus
    | undefined;
  hospitalPrescriptionRefillRequestedPickupDate: string | undefined;
  prescriptionName: string;
  expiresDate: string;
  refills: string;
  notes: string;
  instructions: string;
  salesDate: string;
  veterinarianId: number | undefined;
  veterinarianName: string;
  veterinarianFirstName: string | undefined;
  veterinarianLastName: string | undefined;
  veterinarianLicenseNumber: string | undefined;
  veterinarianLicenseStateId: number | undefined;
  veterinarianLicenseStateAbbreviation: string | undefined;
  veterinarianLicenseExpirationDate: string | undefined;
  veterinarianEmail: string | undefined;
  veterinarianSignaturePhotoId: number | undefined;
  veterinarianSignaturePhotoURL: string | undefined;
  fullName: string;
  status: string;
  unitOfMeasure: string;
  dose: string | undefined;
  frequency: string;
  optionDisplayName: string | undefined;
  clientId: number;
  clientFirstName: string;
  clientLastName: string;
  clientAddress: string;
  clientCity: string;
  clientState: string;
  clientZipcode: string;
  petId: number;
  petName: string;
  petSpecies: string;
  petBreed: string;
  petBirthDate: string;
  petWeight: number;
  quantity: string;
  dateRequested: string;
  dateUpdated: string;
  rowVersion: string;
  hospitalPrescriptionRowVersion: string | undefined;
  fillByDate: string;
  completedDate: string;
  salesItemOptionId: number | undefined;
  orderNotes: string | undefined;
  creatorFirstName: string | undefined;
  creatorLastName: string | undefined;
  creatorTitle: string | undefined;
  dateCreated: string;
  invoiceId: number;
  petcoPrescriptionTypeName:string;
  clientCanSMS: boolean;
}

export class HospitalPrescriptionRequestListingModel
  implements ModelRecord<IHospitalPrescriptionRequestListingModel>
{
  hospitalPrescriptionId: number | undefined;
  hospitalPrescriptionStatusId: HospitalPrescriptionStatus | undefined;
  hospitalPrescriptionRequestSourceId: number | undefined;
  hospitalPrescriptionRefillId: number | undefined;
  hospitalPrescriptionRefillStatusId:
    | HospitalPrescriptionRefillStatus
    | undefined;
  hospitalPrescriptionRefillRequestedPickupDate: DateTime | undefined;
  prescriptionName: string;
  expiresDate: DateTime | undefined;
  refills: string;
  notes: string | undefined;
  instructions: string | undefined;
  salesDate: DateTime;
  veterinarianId: number | undefined;
  veterinarianName: string;
  veterinarianFirstName: string | undefined;
  veterinarianLastName: string | undefined;
  veterinarianLicenseNumber: string | undefined;
  veterinarianLicenseStateId: number | undefined;
  veterinarianLicenseStateAbbreviation: string | undefined;
  veterinarianLicenseExpirationDate: DateTime | undefined;
  veterinarianEmail: string | undefined;
  veterinarianSignaturePhotoId: number | undefined;
  veterinarianSignaturePhotoURL: string | undefined;
  fullName: string;
  status: string;
  unitOfMeasure: string;
  dose: string | undefined;
  frequency: string | undefined;
  optionDisplayName: string | undefined;
  clientId: number;
  clientFirstName: string;
  clientLastName: string;
  clientAddress: string;
  clientCity: string;
  clientState: string;
  clientZipcode: string;
  petId: number;
  petName: string;
  petSpecies: string;
  petBreed: string;
  petBirthDate: string;
  petWeight: number;
  quantity: string;
  dateRequested: string;
  rowVersion: string;
  hospitalPrescriptionRowVersion: string | undefined;
  fillByDate: DateTime | undefined;
  completedDate: DateTime | undefined;
  salesItemOptionId: number | undefined;
  orderNotes: string | undefined;
  creatorFirstName: string | undefined;
  creatorLastName: string | undefined;
  creatorTitle: string | undefined;
  dateCreated: DateTime | undefined;
  dateUpdated: DateTime | undefined;
  invoiceId: number;
  petcoPrescriptionTypeName: string;
  clientCanSMS: boolean;

  constructor(params: IHospitalPrescriptionRequestListingModel) {
    this.hospitalPrescriptionId = defaultTo(
      params?.hospitalPrescriptionId,
      undefined,
    );
    this.hospitalPrescriptionStatusId = defaultTo(
      params?.hospitalPrescriptionStatusId,
      undefined,
    );
    this.hospitalPrescriptionRequestSourceId = defaultTo(
      params?.hospitalPrescriptionRequestSourceId,
      undefined,
    );
    this.hospitalPrescriptionRefillId = defaultTo(
      params?.hospitalPrescriptionRefillId,
      undefined,
    );
    this.hospitalPrescriptionRefillStatusId = defaultTo(
      params?.hospitalPrescriptionRefillStatusId,
      undefined,
    );
    this.hospitalPrescriptionRefillRequestedPickupDate =
      params.hospitalPrescriptionRefillRequestedPickupDate
        ? DateTime.fromISO(
            params?.hospitalPrescriptionRefillRequestedPickupDate,
          )
        : undefined;
    this.prescriptionName = defaultTo(params?.prescriptionName, '');
    this.expiresDate = StringUtility.SanitizeDateTimeISOAsUtc(
      params.expiresDate,
    );
    this.refills = defaultTo(params?.refills, '');
    this.notes = params?.notes;
    this.instructions = params?.instructions;
    this.salesDate = StringUtility.SanitizeDateTimeISOAsUtc(params.salesDate)!;
    this.veterinarianId = defaultTo(params?.veterinarianId, undefined);
    this.veterinarianName = defaultTo(params?.veterinarianName, '');
    this.veterinarianFirstName = params?.veterinarianFirstName;
    this.veterinarianLastName = params?.veterinarianLastName;
    this.veterinarianLicenseNumber = params?.veterinarianLicenseNumber;
    this.veterinarianLicenseStateId = params?.veterinarianLicenseStateId;
    this.veterinarianLicenseStateAbbreviation =
      params?.veterinarianLicenseStateAbbreviation;
    this.veterinarianLicenseExpirationDate =
      params?.veterinarianLicenseExpirationDate
        ? DateTime.fromISO(params?.veterinarianLicenseExpirationDate)
        : undefined;
    this.veterinarianEmail = params?.veterinarianEmail;
    this.veterinarianSignaturePhotoId = params?.veterinarianSignaturePhotoId;
    this.veterinarianSignaturePhotoURL = defaultTo(
      params?.veterinarianSignaturePhotoURL,
      undefined,
    );
    this.fullName = defaultTo(params?.fullName, '');
    this.status = defaultTo(params?.status, '');
    this.unitOfMeasure = defaultTo(params?.unitOfMeasure, '');
    this.dose = params?.dose;
    this.frequency = params?.frequency;
    this.optionDisplayName = defaultTo(params?.optionDisplayName, '');
    this.clientId = defaultTo(params?.clientId, 0);
    this.clientFirstName = defaultTo(params?.clientFirstName, '');
    this.clientLastName = defaultTo(params?.clientLastName, '');
    this.clientAddress = defaultTo(params?.clientAddress, '');
    this.clientCity = defaultTo(params?.clientCity, '');
    this.clientState = defaultTo(params?.clientState, '');
    this.clientZipcode = defaultTo(params?.clientZipcode, '');
    this.petId = defaultTo(params?.petId, 0);
    this.petName = defaultTo(params?.petName, '');
    this.petSpecies = defaultTo(params?.petSpecies, '');
    this.petBreed = defaultTo(params?.petBreed, '');
    this.petBirthDate = defaultTo(params?.petBirthDate, '');
    this.petWeight = defaultTo(params?.petWeight, 0);
    this.quantity = defaultTo(params?.quantity, '');
    this.dateRequested = defaultTo(params?.dateRequested, '');
    this.petcoPrescriptionTypeName = defaultTo(params.petcoPrescriptionTypeName, '')
    this.rowVersion = defaultTo(params?.rowVersion, '');
    this.hospitalPrescriptionRowVersion = defaultTo(
      params?.hospitalPrescriptionRowVersion,
      '',
    );
    this.fillByDate = StringUtility.SanitizeDateTimeISO(params.fillByDate);
    this.completedDate = StringUtility.SanitizeDateTimeISO(
      params.completedDate,
    );
    this.salesItemOptionId = defaultTo(params?.salesItemOptionId, undefined);
    this.orderNotes = defaultTo(params?.orderNotes, undefined);
    this.creatorFirstName = defaultTo(params?.creatorFirstName, '');
    this.creatorLastName = defaultTo(params?.creatorLastName, '');
    this.creatorTitle = defaultTo(params?.creatorTitle, '');
    this.dateCreated = params?.dateRequested
      ? DateTime.fromISO(params?.dateRequested, { zone: 'utc' })
      : undefined;
    this.dateUpdated = params?.dateUpdated
      ? DateTime.fromISO(params?.dateUpdated, { zone: 'utc' })
      : undefined;
    this.invoiceId = defaultTo(params?.invoiceId, 0);
    this.clientCanSMS = defaultTo(params?.clientCanSMS, false);   
  }

  toHospitalPrescriptionModel(): HospitalPrescriptionModel {
    return new HospitalPrescriptionModel({
      hospitalPrescriptionId: this.hospitalPrescriptionId,
      hospitalPrescriptionRequestSourceId:
        this.hospitalPrescriptionRequestSourceId,
      prescribedProduct: this.prescriptionName,
      remainingRefills: defaultTo(Number(this.refills), 0),
      quantity: this.quantity,
      unitOfMeasure: this.unitOfMeasure,
      dose: this.dose,
      frequency: this.frequency,
      instructions: this.instructions,
      expirationDate: defaultTo(this.expiresDate?.toISO(), undefined),
      notes: this.notes,
      salesDate: defaultTo(this.salesDate?.toISO(), undefined),
      veterinarianName: this.veterinarianName,
      veterinarianFirstName: this.veterinarianFirstName,
      veterinarianLastName: this.veterinarianLastName,
      veterinarianLicenseNumber: this.veterinarianLicenseNumber,
      veterinarianLicenseStateId: this.veterinarianLicenseStateId,
      veterinarianLicenseStateAbbreviation:
        this.veterinarianLicenseStateAbbreviation,
      veterinarianLicenseExpirationDate: defaultTo(
        this.veterinarianLicenseExpirationDate?.toISO(),
        undefined,
      ),
      veterinarianEmail: this.veterinarianEmail,
      fullName: this.fullName,
      status: this.status,
      optionDisplayName: this.optionDisplayName,
      fillByDate: defaultTo(this.fillByDate?.toISO(), undefined),
      completedDate: defaultTo(this.completedDate?.toISO(), undefined),
      petId: this.petId,
      veterinarianSignaturePhotoId: this.veterinarianSignaturePhotoId,
      veterinarianSignaturePhoto: this.veterinarianSignaturePhotoURL
        ? new PhotoModel({ photoUrl: this.veterinarianSignaturePhotoURL })
        : undefined,
      veterinarianId: this.veterinarianId,
      salesItemOptionId: this.salesItemOptionId,
      hospitalPrescriptionStatusId: this.hospitalPrescriptionStatusId,
      orderNotes: this.orderNotes,
      rowVersion: this.hospitalPrescriptionRowVersion,
      dateCreated: defaultTo(this.dateCreated?.toISO(), undefined),
      dateUpdated: defaultTo(this.dateUpdated?.toISO(), undefined),
      invoiceId: defaultTo(this.invoiceId, 0)
    });
  }
}
